import PropTypes from 'prop-types';
import React, { Component } from 'react';
import injectSheet from 'react-jss';

import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { injectIntl } from 'react-intl';

import { getPinnedFilters } from '../../Modals/AddFilterModal/AddFilterModal.utils';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import AnalyzerFilterCategory from '../AnalyzerFilterCategory/AnalyzerFilterCategory';
import styles from './AnalyzerFilters.styles';

class AnalyzerFilters extends Component {
  constructor(props) {
    super(props);

    this.handleOnSearchChange = this.handleOnSearchChange.bind(this);
    this.handleFoldersChange = this.handleFoldersChange.bind(this);
    this.handleFilterOptionsChange = this.handleFilterOptionsChange.bind(this);
    this.handleAddNewEntity = this.handleAddNewEntity.bind(this);
    this.state = { query: '', currentModal: null, childCategories: {} };
  }

  handleOnSearchChange(searchValue) {
    this.setState({ query: searchValue, activeCategoryIndex: 0 }, () =>
      this.toggleChildCategories(!!searchValue),
    );
  }

  handleAddNewEntity(entityType, entity) {
    const { filters, updateFilters } = this.props;

    const newEntity =
      entityType === 'section'
        ? { entity: 'section', section: [] }
        : { ...entity, entity: entityType };
    const newFilters = [...filters];

    if (filters.length) {
      const operator = { entity: 'operator', operator: 'and' };
      newFilters.push(operator);
    }

    newFilters.push(newEntity);
    updateFilters(newFilters);
  }

  handleFoldersChange(locations) {
    const newFolders = [...this.state.folders];
    newFolders.push(locations);

    this.setState({ folders: newFolders.flat(Infinity) });
  }

  handleFilterOptionsChange(filterId) {
    this.props.updateFilterOptions(filterId);
  }

  toggleChildCategories(value) {
    Object.values(this.state.childCategories).forEach((child) =>
      child.toggleCategory(value),
    );
  }

  getFilteredCategories(categories) {
    const { query } = this.state;

    return categories.reduce((arr, category) => {
      const matchingFields = category.fields.filter((f) => {
        return f.label.toLowerCase().includes(query.toLowerCase());
      });

      if (matchingFields.length) {
        return [...arr, { ...category, fields: matchingFields }];
      }

      return arr;
    }, []);
  }

  renderTextSearchEntity() {
    const { classes, intl } = this.props;

    const searchAreaOptions = [
      {
        value: 'filename_content',
        label: intl.formatMessage({
          id: 'AnalyzerFilters.filenameAndContentOption',
          defaultMessage: 'Filename and Content',
          description: 'Search area option for filename and content',
        }),
      },
      {
        value: 'filename',
        label: intl.formatMessage({
          id: 'AnalyzerFilters.filenameOption',
          defaultMessage: 'Filename',
          description: 'Search area option for filename only',
        }),
      },
      {
        value: 'content',
        label: intl.formatMessage({
          id: 'AnalyzerFilters.contentOption',
          defaultMessage: 'Content',
          description: 'Search area option for content only',
        }),
      },
    ];

    const basicSearchCategory = {
      title: intl.formatMessage({
        id: 'AnalyzerFilters.basicTextSearchTitle',
        defaultMessage: 'Basic Text Search',
        description: 'Title for basic text search category',
      }),
      fields: searchAreaOptions,
      entityType: 'text_search',
    };
    const booleanSearchCategory = {
      title: intl.formatMessage({
        id: 'AnalyzerFilters.booleanTextSearchTitle',
        defaultMessage: 'Boolean Text Search',
        description: 'Title for boolean text search category',
      }),
      fields: [],
      entityType: 'bool_text_search',
    };

    return (
      <div className={classes.filterEntity}>
        <div className={classes.filterEntityTitle}>
          {intl.formatMessage({
            id: 'AnalyzerFilters.textSearchEntityTitle',
            defaultMessage: 'TEXT SEARCH',
            description: 'Text search entity section title',
          })}
        </div>
        <AnalyzerFilterCategory
          category={basicSearchCategory}
          handleAddNewEntity={this.handleAddNewEntity}
          noPin
        />
        <AnalyzerFilterCategory
          category={booleanSearchCategory}
          handleAddNewEntity={this.handleAddNewEntity}
          noPin
        />
      </div>
    );
  }

  renderFoldersEntity() {
    const { classes, intl } = this.props;

    const folderSearchCategory = {
      title: intl.formatMessage({
        id: 'AnalyzerFilters.filterByFoldersTitle',
        defaultMessage: 'Filter by Folders',
        description: 'Title for filter by folders category',
      }),
      fields: [],
      entityType: 'folder',
    };

    return (
      <div className={classes.filterEntity}>
        <div className={classes.filterEntityTitle}>
          {intl.formatMessage({
            id: 'AnalyzerFilters.foldersEntityTitle',
            defaultMessage: 'FOLDERS',
            description: 'Folders entity section title',
          })}
          <div className={classes.filterEntitySubtitle}>
            {intl.formatMessage({
              id: 'AnalyzerFilters.foldersEntitySubtitle',
              defaultMessage: 'Specify folders in which to run your search.',
              description: 'Subtitle explaining folder search functionality',
            })}
          </div>
        </div>
        <AnalyzerFilterCategory
          category={folderSearchCategory}
          handleAddNewEntity={this.handleAddNewEntity}
          noPin
        />
      </div>
    );
  }

  renderProvisionEntity() {
    const { classes, provisionOptions, intl } = this.props;

    const category = {
      title: intl.formatMessage({
        id: 'AnalyzerFilters.clauseTypesTitle',
        defaultMessage: 'Clause Types',
        description: 'Title for clause types category',
      }),
      fields: provisionOptions,
      entityType: 'provision',
    };

    return (
      <div className={classes.filterEntity}>
        <div className={classes.filterEntityTitle}>
          {intl.formatMessage({
            id: 'AnalyzerFilters.clauseLibraryEntityTitle',
            defaultMessage: 'CLAUSE LIBRARY',
            description: 'Clause library entity section title',
          })}
        </div>
        <AnalyzerFilterCategory
          category={category}
          key={category.title}
          handleAddNewEntity={this.handleAddNewEntity}
          noPin
        />
      </div>
    );
  }

  renderDataFieldsEntity() {
    const { classes, filterOptions, intl } = this.props;
    const { query, childCategories } = this.state;

    const totalFilteredCategories = [
      {
        title: intl.formatMessage({
          id: 'AnalyzerFilters.pinnedCategoryTitle',
          defaultMessage: 'Pinned',
          description: 'Title for pinned filters category',
        }),
        fields: getPinnedFilters(filterOptions),
        expanded: true,
      },
      ...filterOptions,
    ];
    const filteredCategories = this.getFilteredCategories(
      totalFilteredCategories,
    );
    const fieldResultsCount = filteredCategories.reduce(
      (prev, curr) => [...prev, ...curr.fields],
      [],
    ).length;

    return (
      <div className={classes.filterEntity}>
        <div className={classes.filterEntityTitle}>
          {intl.formatMessage({
            id: 'AnalyzerFilters.dataFieldsEntityTitle',
            defaultMessage: 'DATA FIELDS',
            description: 'Data fields entity section title',
          })}
        </div>
        <div className={classes.filterSearch}>
          <SearchInput
            ariaDescribedby="analyzer-filters-found-status"
            name="analyzer-data-fields-search-input"
            value={query}
            onChange={(query) => this.handleOnSearchChange(query)}
            placeholder={intl.formatMessage({
              id: 'AnalyzerSearchFiltersPanel.searchDataFieldsPlaceholder',
              defaultMessage: 'Search data fields…',
              description: 'Placeholder text for data fields search input',
            })}
          />
        </div>
        <div
          className="screenReaderText"
          aria-live="assertive"
          id="analyzer-filters-found-status"
          role="status"
        >
          {!query.length
            ? null
            : fieldResultsCount
              ? intl.formatMessage(
                  {
                    id: 'AnalyzerFilters.itemsFoundMessage',
                    defaultMessage: '{count} item(s) found',
                    description:
                      'Status message showing number of items found. Example: count = "25"',
                  },
                  { count: fieldResultsCount },
                )
              : intl.formatMessage({
                  id: 'AnalyzerFilters.noItemsFoundMessage',
                  defaultMessage: 'No items found',
                  description: 'Status message when no items are found',
                })}
        </div>
        {filteredCategories.map((category) => (
          <AnalyzerFilterCategory
            category={category}
            key={category.title}
            handleFilterOptionsChange={this.handleFilterOptionsChange}
            handleAddNewEntity={this.handleAddNewEntity}
            onRef={(ref, key) => {
              if (ref) {
                childCategories[key] = ref;
              } else {
                delete childCategories[key];
              }
            }}
          />
        ))}
      </div>
    );
  }

  renderLoadingContainer() {
    const { classes, loadingError, loadAnalyzerFiltersData, intl } = this.props;

    let containerContent = <LoadingSpinner size="medium" />;

    if (loadingError) {
      containerContent = (
        <div className={classes.filtersErrorContainer}>
          <div className={classes.filtersErrorMessage}>
            {intl.formatMessage({
              id: 'AnalyzerFilters.loadingErrorMessage',
              defaultMessage:
                "There's been an error with loading filters data.",
              description: 'Error message when filters fail to load',
            })}
          </div>
          <button
            className={classes.filtersErrorAction}
            onClick={() => loadAnalyzerFiltersData()}
          >
            {intl.formatMessage({
              id: 'AnalyzerFilters.retryButton',
              defaultMessage: 'Retry',
              description: 'Button text to retry loading filters',
            })}
          </button>
        </div>
      );
    }

    return <div className={classes.loadingContainer}>{containerContent}</div>;
  }

  render() {
    const { classes, loading, loadingError, intl } = this.props;

    return (
      <div id="analyzer-filters-container" className={classes.analyzerFilters}>
        <div className={classes.filtersHeader}>
          {intl.formatMessage({
            id: 'AnalyzerFilters.filtersHeader',
            defaultMessage: 'Filters',
            description: 'Header text for filters section',
          })}
        </div>
        <div className={classes.filterEntities}>
          {(loading || loadingError) && this.renderLoadingContainer()}
          {this.renderDataFieldsEntity()}
          <hr className={classes.entitySeparator} />
          {this.renderFoldersEntity()}
          <hr className={classes.entitySeparator} />
          {this.renderProvisionEntity()}
          <hr className={classes.entitySeparator} />
          {this.renderTextSearchEntity()}
        </div>
      </div>
    );
  }
}

AnalyzerFilters.propTypes = {
  loading: PropTypes.bool.isRequired,
  loadingError: PropTypes.bool.isRequired,
  filters: PropTypes.array.isRequired,
  filterOptions: PropTypes.array.isRequired,
  provisionOptions: PropTypes.array.isRequired,
  loadAnalyzerFiltersData: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  updateFilterOptions: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectSheet(styles)(injectIntl(AnalyzerFilters));
